import API from "api/base";
import { AxiosResponse } from "axios";
import { useApp } from "components/app";
import { getErrorMessage } from "utils/errorHandling";

export enum RefferalVia {
  wire = "wire",
  credits = "credits",
}

export interface RefferalWidrawnValues {
  via: RefferalVia;
  amount: number;
  code?: string;
  details: {
    email: string;
    iban: string;
  };
}

const sendRefferalWithdrawCode = async (
  values: RefferalWidrawnValues
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await API({
      method: "post",
      url: `/withdraw/code`,
      data: values,
    });

    const data = response.data;
    return data;
  } catch (error: any) {
    return {
      status: false,
      errorMessage: getErrorMessage(error),
    };
  }
};

const sendRefferalWithdrawRequest = async (
  values: RefferalWidrawnValues
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await API({
      method: "post",
      url: `/withdraw/request`,
      data: values,
    });

    const data = response.data;
    return data;
  } catch (e: any) {
    return {
      status: false,
      errorMessage: getErrorMessage(e)
    };
  }
};

const getReferralUserData = async (): Promise<any[]> => {
  try {
    const response = await API({
      method: "get",
      url: `/me/stats`,
    });
    const data = response.data;
    return data;
  } catch (e: any) {
    throw new Error(e);
  }
};

const getRefereesData = async (
  sort: any,
  filter: any,
  pagination: any
): Promise<any> => {
  try {
    const params = {
      order_by: sort.order_by ? sort.order_by : null,
      order_type: sort.order_type ? sort.order_type : null,
      filter_by: filter.filter_by ? filter.filter_by : null,
      filter_value: filter.filter_value ? filter.filter_value : null,
      limit: pagination.limit ? pagination.limit : 10,
      skip: pagination.skip ? pagination.skip : 0,
    };

    const response = await API({
      method: "get",
      url: `/me/referees`,
      params,
    });
    const referees: any[] = [];
    response.data.referees.forEach((referee: any) => {
      const r = {
        user: referee.referee_email,
        status: referee.referee_status ? "Active" : "Inactive",
        registered_at: referee.date,
        activated_at: referee.date_activated
      };
      referees.push(r);
    });
    return {
      data: referees,
      totalRows: response.data.total_count,
      status: {
        active: response.data.active_count,
        inactive: response.data.inactive_count,
      },
    };
  } catch (e: any) {
    throw new Error(e);
  }
};

const getReferralTransactionsData = async (
  sort: any,
  filter: any,
  pagination: any
): Promise<any> => {
  try {
    const params = {
      order_by: sort.order_by ? sort.order_by : null,
      order_type: sort.order_type ? sort.order_type : null,
      filter_by: filter.filter_by ? filter.filter_by : null,
      filter_value: filter.filter_value ? filter.filter_value : null,
      limit: pagination.limit ? pagination.limit : 10,
      skip: pagination.skip ? pagination.skip : 0,
    };

    const response = await API({
      method: "get",
      url: `/me/transactions`,
      params: params,
    });
    const referees: any[] = [];
    response.data.transactions.forEach((transaction: any) => {
      const r = {
        actions: transaction.transaction_data,
        amount: transaction.transaction_value,
        date: transaction.transaction_date,
        type: transaction.transaction_type,
      };
      referees.push(r);
    });
    return {
      data: referees,
      totalRows: response.data.total[0].count,
    };
  } catch (e: any) {
    throw new Error(e);
  }
};

const getFinancialsData = async (
  sort: any,
  filter: any,
  pagination: any
): Promise<any> => {
  try {
    const params = {
      order_by: sort.order_by ? sort.order_by : null,
      order_type: sort.order_type ? sort.order_type : null,
      filter_by: filter.filter_by ? filter.filter_by : null,
      filter_value: filter.filter_value ? filter.filter_value : null,
      limit: pagination.limit ? pagination.limit : 10,
      skip: pagination.skip ? pagination.skip : 0,
    };

    const response = await API({
      method: "get",
      url: `/withdraw/list`,
      params: params,
    });
    const withdraws: any[] = [];
    response.data.transactions.forEach((transaction: any) => {
      const r = {
        withdraw_via: transaction.withdraw_via,
        withdraw_value: transaction.withdraw_value,
        withdraw_status: transaction.withdraw_status,
        withdraw_datetime: transaction.withdraw_datetime,
        withdraw_details: transaction.withdraw_details.email
      };
      withdraws.push(r);
    });
    return {
      data: withdraws,
      totalRows: response.data.total.count,
    };
  } catch (e: any) {
    throw new Error(e);
  }
};

const JWT = {
  getReferralUserData,
  getRefereesData,
  getReferralTransactionsData,
  getFinancialsData,
  sendRefferalWithdrawCode,
  sendRefferalWithdrawRequest,
};

export default JWT;
